/**
 * WaveRiver.jsx — Full-page flowing consciousness waves with time numbers
 *
 * A fixed canvas overlay that renders smooth colorful sinusoidal waves traveling
 * diagonally with time digits drifting within them.
 * Colors: indigo → purple → violet → blue brand palette.
 * Responds to mouse movement. Fixed position = waves visible across all sections.
 */
import React, { useRef, useEffect, useCallback } from 'react';

/* ── Wave definitions — brand palette (dark) — warm amber/gold tones ── */
const WAVE_DEFS = [
  { amp: 55,  freq: 0.008, phase: 0,              speed: 0.012, color: [217, 119, 6],   alpha: 0.16 },
  { amp: 40,  freq: 0.012, phase: Math.PI / 3,    speed: 0.018, color: [245, 158, 11],  alpha: 0.12 },
  { amp: 70,  freq: 0.006, phase: Math.PI / 2,    speed: 0.009, color: [180, 83, 9],    alpha: 0.10 },
  { amp: 45,  freq: 0.010, phase: Math.PI,         speed: 0.015, color: [251, 191, 36],  alpha: 0.08 },
  { amp: 60,  freq: 0.007, phase: Math.PI * 1.3,  speed: 0.011, color: [234, 88, 12],   alpha: 0.07 },
  { amp: 35,  freq: 0.014, phase: Math.PI / 4,    speed: 0.022, color: [252, 211, 77],   alpha: 0.06 },
  { amp: 80,  freq: 0.005, phase: Math.PI * 1.6,  speed: 0.007, color: [146, 64, 14],   alpha: 0.05 },
];

/* ── Wave definitions — brand palette (light) — warm amber/gold tones ── */
const LIGHT_WAVE_DEFS = [
  { amp: 55,  freq: 0.008, phase: 0,              speed: 0.012, color: [217, 119, 6],   alpha: 0.22 },
  { amp: 40,  freq: 0.012, phase: Math.PI / 3,    speed: 0.018, color: [245, 158, 11],  alpha: 0.18 },
  { amp: 70,  freq: 0.006, phase: Math.PI / 2,    speed: 0.009, color: [180, 83, 9],    alpha: 0.16 },
  { amp: 45,  freq: 0.010, phase: Math.PI,         speed: 0.015, color: [251, 191, 36],  alpha: 0.14 },
  { amp: 60,  freq: 0.007, phase: Math.PI * 1.3,  speed: 0.011, color: [146, 64, 14],   alpha: 0.12 },
  { amp: 35,  freq: 0.014, phase: Math.PI / 4,    speed: 0.022, color: [252, 211, 77],   alpha: 0.10 },
  { amp: 80,  freq: 0.005, phase: Math.PI * 1.6,  speed: 0.007, color: [120, 53, 15],   alpha: 0.08 },
];

/* ── Floating time numbers ── */
const MAX_NUMBERS = 18;
const DIGIT_POOL = ['0','1','2','3','4','5','6','7','8','9',':','00','15','30','45',
  '08:30','14:22','09:15','17:45','12:00','23:59','06:00','20:10'];

function spawnNumber(W, H) {
  const char = DIGIT_POOL[Math.floor(Math.random() * DIGIT_POOL.length)];
  // Start from left or top edge, travel to right-bottom
  const fromLeft = Math.random() > 0.4;
  return {
    char,
    x: fromLeft ? -(20 + Math.random() * 40) : Math.random() * W * 0.8,
    y: fromLeft ? Math.random() * H : -(20 + Math.random() * 40),
    vx: 0.2 + Math.random() * 0.45,
    vy: 0.25 + Math.random() * 0.5,
    size: 11 + Math.random() * 16,
    alpha: 0.06 + Math.random() * 0.14,
    born: Date.now(),
    life: 8000 + Math.random() * 10000,
  };
}

export default function WaveRiver({ darkMode = true }) {
  const canvasRef = useRef(null);
  const mouseRef = useRef({ x: 0.5, y: 0.5 });
  const numbersRef = useRef([]);
  const rafRef = useRef(null);
  const timeRef = useRef(0);
  const sizeRef = useRef({ w: 0, h: 0 });
  const darkRef = useRef(darkMode);
  darkRef.current = darkMode;

  /* ── Mouse tracking ── */
  useEffect(() => {
    const onMove = (e) => {
      mouseRef.current.x = e.clientX / window.innerWidth;
      mouseRef.current.y = e.clientY / window.innerHeight;
    };
    window.addEventListener('mousemove', onMove, { passive: true });
    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  /* ── Draw loop ── */
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = sizeRef.current.w;
    const H = sizeRef.current.h;
    if (!W || !H) { rafRef.current = requestAnimationFrame(draw); return; }

    const isDark = darkRef.current;
    const mx = mouseRef.current.x;
    const waveDefs = isDark ? WAVE_DEFS : LIGHT_WAVE_DEFS;

    ctx.clearRect(0, 0, W, H);
    timeRef.current += 1;
    const t = timeRef.current;

    // ── Draw waves ──
    for (let wi = 0; wi < waveDefs.length; wi++) {
      const wave = waveDefs[wi];
      ctx.beginPath();

      const diagonalSlope = 0.3 + wi * 0.04;
      const mouseOffset = (mx - 0.5) * 100;
      const breathe = 0.7 + 0.3 * Math.sin(t * 0.0008 + wi);

      for (let x = 0; x <= W; x += 3) {
        const baseY = H * 0.45;
        const diag = x * diagonalSlope;
        const sine = Math.sin(x * wave.freq + t * wave.speed + wave.phase) * wave.amp * breathe;
        const mWave = Math.sin((x + mouseOffset) * 0.004) * 25;
        const y = baseY + diag + sine + mWave - H * 0.1;

        if (x === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }

      const [r, g, b] = wave.color;
      ctx.strokeStyle = `rgba(${r},${g},${b},${wave.alpha})`;
      ctx.lineWidth = 2;
      ctx.stroke();

      // Subtle gradient fill below wave
      ctx.lineTo(W, H + 10);
      ctx.lineTo(0, H + 10);
      ctx.closePath();
      const grad = ctx.createLinearGradient(0, H * 0.35, 0, H);
      grad.addColorStop(0, `rgba(${r},${g},${b},${wave.alpha * 0.35})`);
      grad.addColorStop(1, `rgba(${r},${g},${b},0)`);
      ctx.fillStyle = grad;
      ctx.fill();
    }

    // ── Time numbers flowing diagonally ──
    const now = Date.now();
    while (numbersRef.current.length < MAX_NUMBERS) {
      numbersRef.current.push(spawnNumber(W, H));
    }

    const textRGB = isDark ? '251,191,36' : '79,70,229';
    numbersRef.current = numbersRef.current.filter(n => {
      const age = now - n.born;
      if (age > n.life) return false;

      n.x += n.vx;
      n.y += n.vy;

      // Offscreen
      if (n.x > W + 60 || n.y > H + 60) return false;

      // Fade in/out
      const lifeP = age / n.life;
      let op = n.alpha;
      if (lifeP < 0.12) op *= lifeP / 0.12;
      else if (lifeP > 0.8) op *= (1 - lifeP) / 0.2;

      ctx.font = `${n.size}px 'Courier New', monospace`;
      ctx.fillStyle = `rgba(${textRGB},${Math.max(0, op).toFixed(3)})`;
      ctx.fillText(n.char, n.x, n.y);
      return true;
    });

    rafRef.current = requestAnimationFrame(draw);
  }, []);

  /* ── Sizing & lifecycle ── */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = window.innerWidth;
      const h = window.innerHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = w + 'px';
      canvas.style.height = h + 'px';
      const ctx = canvas.getContext('2d');
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      sizeRef.current = { w, h };
    };

    resize();
    window.addEventListener('resize', resize, { passive: true });
    rafRef.current = requestAnimationFrame(draw);

    return () => {
      window.removeEventListener('resize', resize);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [draw]);

  return (
    <canvas
      ref={canvasRef}
      className="pointer-events-none"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 1,
      }}
    />
  );
}
